import { Suspense, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import c from 'classnames';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useActionPlanData from 'services/actionPlan/actionPlanData';
import {
  getSingleConditionMetricsQuery,
  type SingleConditionData,
} from 'services/conditions/useConditionMetrics';
import { getConditionActionPlanQuery } from 'services/conditions/conditionsActionPlan';

import { NewCondition } from 'types/Conditions';
import { FeedbackPageType } from 'types/Feedback';

import {
  Spinfinity,
  FeedbackWidget,
  Card,
  FAQ,
  Markdown,
  Typography,
  Icon,
  DesignButton,
  ButtonColors,
  ButtonVariants,
  PageContainer,
  ContainerSizes,
} from 'components';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';

import { ConditionMetricsAssociated } from './ConditionMetricsAssociated';
import { ConditionMetricActions } from './ConditionMetricActions';

import styles from './ConditionMetricsDetailView.module.scss';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { RelatedActions } from '../Insights/SubMetricDetail/SubMetricDetail';
import { getActionsForSubMetric } from './ConditionMetricRow';
import { ActionPlanItem } from 'types/ActionPlan';

export const ConditionMetricsDetailView = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { condition } = useParams<{ condition: string }>();
  if (!condition) throw new Error('No condition provided');

  const { data: conditionData } = useSuspenseQuery<SingleConditionData | null>(
    getSingleConditionMetricsQuery(currentKitId, condition),
  );

  const { data: actionPlanData } = useActionPlanData({ kitId: currentKitId });
  const { data: conditionActionPlanData } = useSuspenseQuery(
    getConditionActionPlanQuery(currentKitId),
  );

  // Action items to show in the second column
  const actionItems = useMemo(() => {
    const seenIds = new Set<number | string>();
    const uniqueActions: ActionPlanItem[] = [];

    (conditionData?.metrics ?? []).forEach(m => {
      const actionsForMetric = getActionsForSubMetric(
        actionPlanData ?? [],
        m.metric_id,
      );

      actionsForMetric.forEach(action => {
        if (!seenIds.has(action.id)) {
          seenIds.add(action.id);
          uniqueActions.push(action);
        }
      });
    });

    return uniqueActions;
  }, [actionPlanData, conditionData]);

  const currentCondition: NewCondition | null = conditionData
    ? ({
        condition_id: conditionData.condition_id,
        condition: conditionData.condition,
        condition_display_name: conditionData.condition_display_name,
        about: conditionData.about,
        faq: conditionData.faq,
        references: conditionData.references,
        id: conditionData.condition_id,
      } as any)
    : null;

  // Map action plan items to the format expected by ConditionMetricActions
  // Uses conditionActionPlanData for ordering, then enriches with actionPlanData details
  // Match logic: check if condition name contains the action's tags (backend logic in condition.ts line 473)
  const matchedActions = useMemo(() => {
    if (!actionPlanData || !conditionActionPlanData) return [];
    return conditionActionPlanData
      .map((action: any) => {
        const matchedAction = actionPlanData.find(
          (actionPlanItem: any) =>
            actionPlanItem.id === +action.action_plan_recommendations_id,
        );
        return matchedAction
          ? {
              ...action,
              display_title: matchedAction.display_title,
              category: matchedAction.category,
            }
          : null;
      })
      .filter((action: any) => {
        // Check if the condition name contains the action's tags
        return condition.includes(action?.tags || '');
      })
      .filter((action: any) => action !== null);
  }, [actionPlanData, conditionActionPlanData, condition]);

  const actionsRef = useRef<HTMLDivElement>(null);

  if (!currentKitId || !currentCondition) return <Spinfinity />;

  return (
    <PageContainer size={ContainerSizes.LG}>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [conditionData?.condition ?? '']:
            conditionData?.condition_display_name,
        }}
      />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <div className={styles.contentContainer}>
            <Suspense fallback={<Spinfinity />}>
              {matchedActions.length > 0 && (
                <Card className={c(styles.actionsReminder, 'hide-on-desktop')}>
                  <div
                    className='flex gap-2'
                    onClick={() => {
                      if (actionsRef.current) {
                        actionsRef.current.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }
                    }}
                  >
                    <div className={styles.iconWrapper}>
                      <Icon name='flashFilled' size='m' />
                    </div>
                    <div className='flex flex-column gap-1'>
                      <Typography variant='heading-s'>{`${matchedActions.length} actions related to this metric`}</Typography>
                      <DesignButton
                        label='Jump to actions'
                        iconNameRight='chevronForward'
                        color={ButtonColors.WHITE}
                        variant={ButtonVariants.TEXT}
                      />
                    </div>
                  </div>
                </Card>
              )}
              <ConditionMetricsAssociated
                condition={currentCondition}
                conditionData={conditionData}
              />
            </Suspense>

            {!!currentCondition?.about && (
              <Card variant='wrapper' className={styles.aboutCard}>
                <div className={styles.secondaryText}>
                  <Markdown
                    customOverride={{
                      h3: ({ children }: { children: string }) => (
                        <Typography variant='heading-l'>{children}</Typography>
                      ),
                    }}
                  >
                    {currentCondition.about}
                  </Markdown>
                </div>
              </Card>
            )}
            <div ref={actionsRef} className='hide-on-desktop'>
              <RelatedActions actions={actionItems} isCondition />
            </div>
            {currentCondition?.faq && (
              <Card className={styles.faqCard} variant='wrapper'>
                <Typography variant='heading-l'>
                  Frequently Asked Questions
                </Typography>
                <FAQ faq={currentCondition.faq} hideLastDivider />
              </Card>
            )}
            {currentCondition?.references && (
              <Card className={styles.referenceCard} variant='wrapper'>
                <Typography
                  variant='heading-l'
                  className={styles.referenceTitle}
                >
                  References
                </Typography>
                <Markdown>{currentCondition.references}</Markdown>
              </Card>
            )}
            <FeedbackWidget
              kitId={currentKitId}
              pageType={FeedbackPageType.CONDITION}
              pageId={currentCondition?.id}
            />
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <div className='hide-on-mobile'>
            <RelatedActions actions={actionItems} isCondition />
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
